import CheckBox from "@/app/components/ui/CheckBoxes/FormCheckbox";
import Input from "@/app/components/ui/Inputs/Input";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useBOMSetupCityListAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSetupCityListAPI";
import { useBOMSetupDetailAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSetupDetailAPI";
import { useBOMSetupUpdateAPI } from "@/app/hooks/api_hooks/Inventory/BOMSetup/useBOMSetupUpdateAPI";
import { useConfigurationClinicListTableAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationClinicListTableAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaArrowDown } from "react-icons/fa6";
import { useNavigate, useSearchParams } from "react-router-dom";

export interface IBOMFormClinicMappingFields {
  city: string[];
  display: { [clinicId: string]: boolean };
  allDisplay: boolean;
  price: { [clinicId: string]: number };
}

const BomClinicPricingMapping = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { forOnlyNumbers } = useFormValidations();
  const { execute: fetchBomCityList, bomCityList } = useBOMSetupCityListAPI();
  const [params, _setParams] = useSearchParams();
  const { execute: updateBOM } = useBOMSetupUpdateAPI();
  const [bomCities, setBomCities] = useState<any>();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { cities } = routeState.state as IInventoryState;
  const cityItems = useSelectMenuReducer(cities, "name", "id");
  const bom_id = params.get("bom_id");
  const fetchOrgCities = useOrgCitiesListAPI("INVENTORY");
  const fetchClinicList = useConfigurationClinicListTableAPI();
  const { execute: fetchBomDetail, bomDetail } = useBOMSetupDetailAPI();
  const [persistentDisplay, setPersistentDisplay] = useState<Record<string, boolean>>({});
  const [cliniclist, setClinicList] = useState([]);

  const handlePriceWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.currentTarget.blur(); // remove focus to avoid scroll increment
  };

  const handlePriceArrowKeys = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
    }
  };

  const updatePersistentDisplay = (clinicId: string, value: boolean) => {
    setPersistentDisplay((prev) => ({
      ...prev,
      [clinicId]: value,
    }));
  };

  useEffect(() => {
    fetchOrgCities();
    fetchClinicList();
    if (bom_id) {
      fetchBomDetail(bom_id);
    }
  }, [bom_id]);

  useEffect(() => {
    if (bomCityList?.length > 0) {
      if (cliniclist.length === 0) {
        setClinicList(bomCityList);
      }
      const updatedCities = bomCityList.map((city) => {
        const existingDisplay = persistentDisplay[city.clinic_id];
        return {
          ...city,
          display: existingDisplay !== undefined ? existingDisplay : false,
        };
      });
      setBomCities(updatedCities);
    }
  }, [bomCityList]);

  const formHook = useForm<IBOMFormClinicMappingFields>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    const hasBomDetail = bomDetail && typeof bomDetail === "object" && Object.keys(bomDetail).length > 0;
    if (hasBomDetail) {
      if (bomDetail?.clinic_mapping?.length > 0) {
        const updatedBomCities = bomDetail.clinic_mapping.map((c) => {
          const existingDisplay = persistentDisplay[c.clinic_id];
          const displayValue = existingDisplay !== undefined ? existingDisplay : c.display === true;
          updatePersistentDisplay(c.clinic_id, displayValue);
          return { ...c, display: displayValue };
        });
        setBomCities(updatedBomCities);
        const displayItems = {} as { string: boolean };
        const priceItems = {} as { string: number };
        updatedBomCities.map((c) => {
          displayItems[c.clinic_id] = c.display;
          priceItems[c.clinic_id] = c.price;
        });
        formHook.setValue("display", displayItems);
        formHook.setValue("price", priceItems);
      } else {
        fetchBomCityList([]);
      }
    }
  }, [bomDetail]);

  const onCheckChangeHandler = (clinicId: string, value: boolean) => {
    formHook.setValue(`display.${clinicId}`, value);
    updatePersistentDisplay(clinicId, value);
  };

  const onSubmit = () => {
    const allClinics = cliniclist.map((b) => {
      const clinicId = b.clinic_id;
      return {
        clinic_name: b.clinic_name,
        clinic_id: clinicId,
        display: formHook.watch(`display.${clinicId}`),
        price: +formHook.watch(`price.${clinicId}`) || +b.price || 0,
      };
    });
    const shouldSendLabDetails = bomDetail?.general_details?.service_details?.bom_type === "Lab";
    const shouldSendVaccinationDetails = bomDetail?.general_details?.service_details?.bom_type === "Vaccine";
    const constructedBody = {
      general_details: {
        comments: bomDetail?.general_details?.comments,
        invoice: bomDetail?.general_details?.invoice,
        pre_requisites: bomDetail?.general_details?.pre_requisites,
        document_type: bomDetail?.general_details?.document_type,
        service_details: {
          bom_type: bomDetail?.general_details?.service_details?.bom_type,
          bom_name: bomDetail?.general_details?.service_details?.bom_name,
        },
        product_component: bomDetail?.general_details?.product_component?.map((p) => {
          return { sku_name: p.sku_name, sku_id: p.sku_id, qty_per_pkg: parseInt(`${p.qty_per_pkg}`), qty: p.qty };
        }),
        pet_details: {
          pet_species: bomDetail?.general_details?.pet_details?.pet_species,
          pet_age_range: {
            pet_age_min: parseFloat(bomDetail?.general_details?.pet_details?.pet_age_range?.pet_age_min),
            pet_age_max: parseFloat(bomDetail?.general_details?.pet_details?.pet_age_range?.pet_age_max),
          },
          pet_weight_range: {
            pet_weight_min: parseFloat(bomDetail?.general_details?.pet_details?.pet_weight_range?.pet_weight_min),
            pet_weight_max: parseFloat(bomDetail?.general_details?.pet_details?.pet_weight_range?.pet_weight_max),
          },
        },
        ...(shouldSendLabDetails
          ? {
              lab_details: {
                sample_type: bomDetail?.general_details?.lab_details?.sample_type,
                vial_type: bomDetail?.general_details?.lab_details?.vial_type,
                sample_quantity: bomDetail?.general_details?.lab_details?.sample_quantity,
                parameters_included: bomDetail?.general_details?.lab_details?.parameters_included,
              },
            }
          : {}),
        ...(shouldSendVaccinationDetails
          ? {
              vaccination_details: {
                vaccine_name: bomDetail?.general_details?.vaccination_details?.vaccine_name || bomDetail?.general_details?.vaccination_details?.family_of_vaccine,
                vaccine_id: bomDetail?.general_details?.vaccination_details?.vaccine_id,
                dose_name: bomDetail?.general_details?.vaccination_details?.dose_name || bomDetail?.general_details?.vaccination_details?.dose,
                dose_id: bomDetail?.general_details?.vaccination_details?.dose_id,
              },
            }
          : {}),
      },
      vms_category_id: bomDetail?.vms_category_id,
      vms_category_name: bomDetail?.vms_category_name,
      vms_subcategory_name: bomDetail?.vms_subcategory_name,
      vms_subcategory_id: bomDetail?.vms_subcategory_id,
      status: bomDetail?.status ? true : false,
      clinic_mapping: allClinics,
    };
    updateBOM(constructedBody, params.get("bom_id")).then(({ status }) => {
      if (status) {
        navigate(`/app/inventory/bom-setup`);
      }
    });
  };

  const handelBackStepper = () => {
    navigate(`/app/inventory/bom-onboard?page_step=general&&bom_id=${bom_id}`);
  };

  return (
    <>
      <form className="mt-2 w-full" onSubmit={formHook.handleSubmit(onSubmit)}>
        <div className="mt-3 px-8 bg-white sm:px-6 lg:px-8 py-5">
          <div className="flex gap-5">
            <div className="w-80">
              <SearchableMultiSelectMenu
                withSelectAll
                errorMessages={[]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue(
                      "city",
                      item.map((c) => c.id),
                    );
                  }
                }}
                fieldError={formHook.formState.errors.city}
                register={formHook.register("city")}
                selectItems={cityItems}
                placeholder="Select City"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={cityItems?.filter((c) => formHook.watch("city")?.includes(c.id))}
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
            <div>
              <input
                type="button"
                onClick={() => {
                  fetchBomCityList(formHook.watch("city"));
                }}
                className={`py-2 px-6 border-transparent rounded-md text-sm cursor-pointer bg-black text-white`}
                value={"Apply Filter"}
              />
            </div>
          </div>
        </div>
        <div className="mt-3 px-8 bg-white sm:px-6 lg:px-8 py-5">
          <div className="shadow-md mt-9">
            <table className="divide-y divide-gray-200 border rounded-md w-full">
              <thead className="bg-[#f5f5f5] sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-black font-medium">
                    <CheckBox
                      errorMessages={[]}
                      fieldError={formHook.formState.errors.display}
                      register={formHook.register("allDisplay")}
                      onCheckChange={(item) => {
                        const obj = {};
                        cliniclist.map((c) => (obj[c.clinic_id] = true));
                        if (item) {
                          formHook.setValue("display", obj);
                        } else {
                          const obj = {};
                          cliniclist.map((c) => (obj[c.clinic_id] = false));
                          formHook.setValue("display", obj);
                        }
                        formHook.setValue("allDisplay", !!item);
                      }}
                    />
                  </th>
                  <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-black font-medium">Clinic Name</th>
                  <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-black font-medium">City</th>
                  <th className="px-3 py-3 text-left whitespace-nowrap text-sm text-black font-medium">Add Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-100">
                {bomCities?.map((city, index) => (
                  <tr key={city.clinic_id}>
                    <td className={"px-3 py-4 text-xs whitespace-nowrap font-normal text-left"}>
                      <CheckBox
                        errorMessages={[]}
                        onCheckChange={(item) => onCheckChangeHandler(city.clinic_id, item)}
                        defaultChecked={formHook.watch(`display.${city.clinic_id}`)}
                        fieldError={formHook.formState.errors.display}
                        register={formHook.register(`display.${city.clinic_id}`)}
                      />
                    </td>
                    <td className={"px-3 py-4 text-xs whitespace-nowrap font-normal text-left"}>{city.clinic_name}</td>
                    <td className={"px-3 py-4 text-xs whitespace-nowrap font-normal text-left"}>{city.city_name}</td>
                    <td
                      className={"px-3 py-4 text-xs whitespace-nowrap font-normal text-left flex gap-10 items-baseline"}
                    >
                      <Input
                        register={formHook?.register(`price.${city.clinic_id}`, {
                          ...forOnlyNumbers.validations,
                        })}
                        type="number"
                        min={0}
                        onWheel={handlePriceWheel}
                        onKeyDown={handlePriceArrowKeys}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          formHook.setValue(`price.${city.clinic_id}`, +event.target.value);
                        }}
                        fieldError={formHook?.formState?.errors?.price}
                        errorMessages={[forOnlyNumbers.errors]}
                        className="border-slate-400 rounded-md w-[150px] h-9 text-sm"
                        placeholder="Enter Price"
                      />
                      {index == 0 && (
                        <button
                          className="text-blue-500 font-medium flex gap-2"
                          onClick={(e) => {
                            e.preventDefault();
                            const copyPrice = formHook.watch(`price.${city.clinic_id}`);
                            bomCities.map((b) => {
                              formHook.setValue(`price.${b.clinic_id}`, copyPrice);
                            });
                          }}
                        >
                          <FaArrowDown className="w-4 h-4" />
                          <p className="text-sm">Copy this price below</p>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="right-5 bottom-0 sticky flex justify-end items-center gap-4 p-3 z-50 bg-white border-t-2">
          <div
            onClick={handelBackStepper}
            className="flex justify-center items-center gap-2 bg-slate-400 bg-opacity-10 px-10 py-[9px] border rounded-md h-9 cursor-pointer"
          >
            <div className="font-medium text-black text-xs">Back</div>
          </div>
          <input
            type="submit"
            value={"Save"}
            className="flex justify-center items-center gap-2 border-slate-400 bg-sky-900 px-10 py-[9px] border border-opacity-20 rounded-md h-9 font-medium text-white text-xs cursor-pointer"
            placeholder="Save"
          />
        </div>
      </form>
    </>
  );
};
export default BomClinicPricingMapping;
